import { Market } from '../context/MarketContext';
import { cimmpleFlowIndiaPricing, cimmpleFlowUsPricing } from './cimmpleFlowPricing';
import { cimmpleRxStartingPrice } from './cimmpleRxPricing';

export type ProductColor = 'primary' | 'accent';
export type ProductIcon = 'operations' | 'payroll' | 'pharmacy';

export interface ProductData {
  id: string;
  markets: Market[];
  name: string;
  tagline: string;
  description: string;
  longDescription: string;
  icon: ProductIcon;
  screenshot?: string;
  screenshotAlt?: string;
  features: { title: string; description: string }[];
  benefits: string[];
  useCases: string[];
  color: ProductColor;
  prices: Partial<Record<Market, string>>;
}

export const products: ProductData[] = [
  {
    id: 'cimmpleflow',
    markets: ['US', 'IN'],
    name: 'CimmpleFlow',
    tagline: 'A modern cloud operating system for machine shops',
    description: 'Run your entire machine shop from one cloud platform—quoting, purchasing, production, quality, inventory, accounting, documents, and AI-powered automation together. Includes a complete manufacturing ERP without the legacy complexity.',
    longDescription: `CimmpleFlow is a modern cloud operating system for machine shops and precision manufacturers. It replaces disconnected spreadsheets, legacy ERP, paper travelers, and manual processes with one connected platform you can access anywhere.

From faster quoting with VendorConnect™ to live job tracking, quality records, inventory, accounting, and AI invoice extraction, everything works together—so your team spends less time on paperwork and more time shipping parts.`,
    icon: 'operations',
    screenshot: '/images/platform/cimmpleflow-home.png',
    screenshotAlt: 'CimmpleFlow dashboard showing orders, jobs, revenue, and operational analytics',
    features: [
      { title: 'Faster Quoting', description: 'Quote faster with live costs and VendorConnect™ collaboration—without chasing spreadsheets.' },
      { title: 'Order Visibility', description: 'See every order from intake to shipment with clear status and fewer follow-up emails.' },
      { title: 'Smarter Purchasing', description: 'Know exactly what to buy and when, without spreadsheets or last-minute shortages.' },
      { title: 'Live Job Tracking', description: 'Track every job in real time from quote to shipment—scheduling, status, and shop-floor updates in one place.' },
      { title: 'Quality & Traceability', description: 'Deliver parts with complete traceability and quality records that travel with the job.' },
      { title: 'Scheduling & Utilization', description: 'Improve scheduling and machine utilization with live visibility across the shop.' },
      { title: 'Shop Accounting', description: 'Keep finances current with invoicing, expenses, and reporting tied to real shop activity.' },
      { title: 'AI Invoice Capture', description: 'Cut AP busywork—upload a PDF or forward vendor email and extract invoice data automatically into forms.' },
      { title: 'Time & Costing', description: 'Capture time with third-party integrations for payroll and accurate job costing.' },
      { title: 'Document Management', description: 'Keep travelers, drawings, and quality docs versioned and easy to find—less paper, fewer mistakes.' },
    ],
    benefits: [
      'Run the whole shop from one cloud platform',
      'Less paperwork and fewer delays',
      'Live visibility from quote to shipment',
      'Faster quoting and better customer communication',
      'Higher machine utilization with clearer scheduling',
      'AI-assisted automation for invoices and admin work',
      'Fast to deploy, easy to learn, accessible anywhere',
    ],
    useCases: [
      'CNC machine shops',
      'Precision manufacturers',
      'Job shops and contract manufacturers',
      'Engineering and prototype shops',
      'Manufacturing companies',
      'Logistics and distribution teams',
    ],
    color: 'primary',
    prices: {
      US: cimmpleFlowUsPricing.priceLabel,
      IN: cimmpleFlowIndiaPricing.priceLabel,
    },
  },
  {
    id: 'cimmlepay',
    markets: ['US'],
    name: 'CimmplePay',
    tagline: 'Payroll Processing, Simplified',
    description: 'An intelligent payroll management solution that automates salary processing, tax calculations, and compliance reporting, so you can focus on what matters most—your people.',
    longDescription: `CimmplePay is our upcoming payroll management solution designed to take the complexity out of paying your employees. Once launched, it will handle everything from salary calculations and tax withholdings to biometric attendance and year-end reporting.

With built-in tax tables that will be automatically updated, CimmplePay will ensure your payroll is always compliant with the latest regulations. The employee self-service portal will empower your team to access their information, reducing HR workload.`,
    icon: 'payroll',
    screenshot: '/images/platform/cimmplepay-home.png',
    screenshotAlt: 'CimmplePay payroll management dashboard',
    features: [
      { title: 'Automated Calculations', description: 'Calculate salaries, overtime, bonuses, and deductions automatically with precision.' },
      { title: 'Tax Compliance', description: 'Stay compliant with automatic tax calculations, withholdings, and filings.' },
      { title: 'Employee Self-Service', description: 'Let employees view pay stubs, update information, and access tax documents.' },
      { title: 'Biometric Face-Recognition Attendance', description: 'Clock in and out with secure face recognition for accurate, fraud-resistant attendance tracking.' },
      { title: 'Leave Management', description: 'Track vacation, sick leave, and PTO with automated accrual calculations.' },
      { title: 'Comprehensive Reporting', description: 'Generate detailed reports for payroll, taxes, labor costs, and compliance.' },
    ],
    benefits: [
      'Process payroll in minutes, not hours',
      'Achieve 100% tax compliance',
      'Eliminate manual calculation errors',
      'Ensure employees are paid on time',
      'Reduce HR administrative workload',
      'Access payroll data from anywhere',
    ],
    useCases: [
      'Small and medium businesses',
      'Growing startups',
      'Multi-state employers',
      'Companies with hourly workers',
      'Organizations with complex pay structures',
      'Businesses with remote teams',
    ],
    color: 'accent',
    prices: { US: '$29/month' },
  },
  {
    id: 'cimmplerx',
    markets: ['IN'],
    name: 'CimmpleRx',
    tagline: 'A Smarter ERP for Pharmacies',
    description: 'Manage pharmacy billing, batch-wise inventory, purchases, accounting, GST, customers, and vendors from one connected system.',
    longDescription: `CimmpleRx is a pharmacy ERP built for medical shops, wholesalers, and agencies in India. It brings billing, inventory, purchases, customer and vendor records, payments, and accounting together in one practical workspace.

Track medicines by batch and expiry date, stay ahead of low stock, manage GST-ready transactions, and understand daily sales at a glance. Role-based access and shop-level data isolation help every team work securely with the information they need.`,
    icon: 'pharmacy',
    features: [
      { title: 'Pharmacy Billing', description: 'Run point-of-sale billing, generate invoices, and accept cash, card, UPI, or credit payments.' },
      { title: 'Batch & Expiry Tracking', description: 'Track medicine batches, manufacturing details, expiry dates, and expiring stock.' },
      { title: 'Inventory Management', description: 'See stock in real time, set reorder points, and receive low-stock notifications.' },
      { title: 'Smart Ordering', description: 'Use sales velocity, stock levels, and vendor schedules to prepare suggested purchase orders.' },
      { title: 'Purchase Management', description: 'Create purchase orders and manage them from draft through confirmation and receipt.' },
      { title: 'GST & Accounting', description: 'Manage your chart of accounts, journal entries, GST calculations, and GST reports.' },
      { title: 'Customer & Vendor Management', description: 'Maintain customer and vendor records, GSTIN details, credit limits, and outstanding balances.' },
      { title: 'Reports & Analytics', description: 'Review sales, purchases, payment methods, inventory value, and operational trends.' },
      { title: 'Role-Based Access', description: 'Give admins, managers, accountants, and salespeople access appropriate to their responsibilities.' },
    ],
    benefits: [
      'One workspace for daily pharmacy operations',
      'Real-time stock and sales visibility',
      'Fewer losses from expired medicines',
      'Faster counter billing and collections',
      'Timely, data-informed purchase planning',
      'GST-ready financial records and reports',
      'Secure access for each team role',
    ],
    useCases: [
      'Retail pharmacies',
      'Medical shops',
      'Pharmacy chains',
      'Medicine wholesalers',
      'Pharmaceutical agencies',
      'Multi-branch operations',
    ],
    color: 'accent',
    prices: { IN: cimmpleRxStartingPrice },
  },
];

export const getProductsForMarket = (market: Market): ProductData[] =>
  products.filter((product) => product.markets.includes(market));

export const getProductById = (productId: string): ProductData | undefined =>
  products.find((product) => product.id === productId);

export const getProductPrice = (product: ProductData, market: Market): string =>
  product.prices[market] ?? product.prices.US ?? '';
